import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Utils
import { elementClassNames, getDomElementId } from '../utils/elementUtil';
import { getBorderRenderedValues, getHotTableTotalWidthGU } from './utils/tableSizeUtils';
import {
    getElementId,
    getShowTitle,
    getTableContentColWidthsGU,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getElementStyle } from '../../utils/grid/gridUtils';
import { getCaption, getShowCaption } from '../../components/caption/elementCaptionUtil';
import { getIsFeatureEnabled } from '../feature/elementFeatureUtils';
import { getShowQuickLineTool } from '../quickLine/quickLineUtil';

// Components
import Caption from '../../components/caption/Caption';
import QuickLineCreationTool from '../quickLine/QuickLineCreationTool';
import ElementReactions from '../reactions/ElementReactions';
import AttachedCommentsDropTarget from '../dnd/elementDropTargets/AttachedCommentsDropTarget';
import AttachedCommentsContainer from '../attachedComment/AttachedCommentsContainer';
import ElementActivityIndicator from '../activity/ElementActivityIndicator';
import ElementUserTag from '../../user/userActivity/ElementUserTag';
import TableTitleEditor from './TableTitleEditor';
import PresentationalTable from './PresentationalTable';
import HotTable from './HotTable';
import ElementResizeHandle from '../resizing/ElementResizeHandle';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { ELEMENT_FEATURES } from '../feature/elementFeatureConstants';
import { TABLE_CAPTION_EDITOR_KEY } from '../../../common/table/tableConstants';
import { IN_LIST_MODE } from '../../../common/columns/columnConstants';

// Styles
import './Table.scss';

const Table = (props) => {
    const {
        element,
        gridSize,
        inList,
        inTrash,
        isLocked,
        isEditable,
        elementEvents,
        isSelected,
        isDragging,
        isPresentational,
        isRemotelySelected,
        currentBoardId,
        permissions,
        setParentHoveredChildAcceptsDrop,
        getContextZoomScale,
        getContextZoomTranslationPx,
        dispatchSetCellSelection,
        currentCellSelections,
    } = props;

    const tableRef = useRef(null);
    const tableTitleRef = useRef(null);
    const hotTableRef = useRef(null);

    const hotTableInstanceRef = useRef(null);
    const hotTableContainerRef = useRef(null);

    const showTitle = getShowTitle(element);
    const showCaption = getShowCaption(element);
    const isTransparencyEnabled = getIsFeatureEnabled(ELEMENT_FEATURES.TRANSPARENT)(element);

    const elementId = getElementId(element);
    const captionContent = getCaption(element);

    const hotTableWidthGU = getHotTableTotalWidthGU(getTableContentColWidthsGU(element), gridSize);
    const tableWidth = hotTableWidthGU || ELEMENT_DEFAULT_WIDTH;

    const transparent = isTransparencyEnabled;

    const tableClasses = useMemo(
        () =>
            classNames('Table drag-handle contrast-shadow', {
                'background-color-target': isSelected,
                'background-color-contrast-target': isSelected,
                'show-title': showTitle,
                'show-caption': showCaption,
                'has-cell-selection': currentCellSelections?.size,
            }),
        [showTitle, showCaption, isSelected, hotTableInstanceRef?.current?.getSelected(), currentCellSelections?.size],
    );

    const classes = elementClassNames(tableClasses, { ...props, transparent });

    const elementStyle = getElementStyle(tableWidth, gridSize, inList);

    const calculateTitleHeight = useCallback(() => {
        if (isPresentational || !tableRef.current || !tableTitleRef.current) return;

        const zoomScale = getContextZoomScale ? getContextZoomScale() : 1;
        const newTitleHeight = (tableTitleRef.current.getBoundingClientRect().height / zoomScale).toFixed();
        const titlePositionAdjustment = newTitleHeight % gridSize;

        tableRef.current.style.setProperty('--table-title-height', `${newTitleHeight - titlePositionAdjustment}px`);
        tableRef.current.style.setProperty('--table-title-position-adjustment', `${titlePositionAdjustment}px`);
    }, [gridSize]);

    useEffect(() => {
        if (showTitle) {
            tableRef.current.style.setProperty('--table-title-height', `${5 * gridSize}px`);
            calculateTitleHeight();
        } else {
            tableRef.current.style.setProperty('--table-title-height', '0px');
        }
    }, [showTitle]);

    useEffect(() => {
        calculateTitleHeight();
    }, [gridSize]);

    const jumpToFirstCell = useCallback(
        () => dispatchSetCellSelection(elementId, [0, 0, 0, 0]),
        [elementId, dispatchSetCellSelection],
    );

    useEffect(() => {
        if (!hotTableInstanceRef.current || !tableRef.current) return;

        const firstTdElement = hotTableInstanceRef.current.rootElement.querySelector('td');
        const { actualBorderWidth, missingBorderWidth } = getBorderRenderedValues(firstTdElement);
        tableRef.current.style.setProperty('--1px-border-rendered-width', `${actualBorderWidth}px`);
        tableRef.current.style.setProperty('--1px-border-missing-width', `${missingBorderWidth}px`);
    }, []);

    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        const scrollElement = hotTableInstanceRef.current.rootElement.querySelector('.wtHolder');
        const scrollBarHeight = scrollElement.offsetHeight - scrollElement.clientHeight;
        tableRef.current.style.setProperty('--scroll-bar-height', `${scrollBarHeight}px`);
    }, []);

    const showPresentational =
        !isRemotelySelected && // If element is remotely selected, we want to allow the user to see updates
        (isPresentational || inList === IN_LIST_MODE.IN_LIST_DOCUMENT_MODE) && // If the element is in mobile view, we want to show the presentational table
        !!document.querySelector(`#el-${elementId}`); // If the element is not in the dom, we don't want to show the presentational table
    const showResizeHandle = !isLocked && isEditable && !inList;

    return (
        <div id={getDomElementId(elementId)} ref={tableRef} className={classes} {...elementEvents} style={elementStyle}>
            {showTitle && (
                <TableTitleEditor
                    {...props}
                    ref={tableTitleRef}
                    onChange={() => {
                        requestAnimationFrame(calculateTitleHeight);
                    }}
                    jumpToFirstCell={jumpToFirstCell}
                />
            )}
            {showPresentational && (
                <PresentationalTable
                    element={element}
                    gridSize={gridSize}
                    inList={inList}
                    isDragging={isDragging}
                    inTrash={inTrash}
                />
            )}
            {!showPresentational && (
                <HotTable
                    {...props}
                    ref={hotTableRef}
                    hotTableInstanceRef={hotTableInstanceRef}
                    hotTableContainerRef={hotTableContainerRef}
                    showTitle={showTitle}
                    showCaption={showCaption}
                />
            )}

            <QuickLineCreationTool
                show={getShowQuickLineTool(props)}
                elementId={elementId}
                element={element}
                currentBoardId={currentBoardId}
                gridSize={gridSize}
                getContextZoomScale={getContextZoomScale}
                getContextZoomTranslationPx={getContextZoomTranslationPx}
            />
            <ElementActivityIndicator {...props} />
            <ElementUserTag {...props} />
            <ElementReactions {...props} />
            <Caption
                {...props}
                padTop
                placeholder="Add a description"
                textContent={captionContent}
                captionVisible={showCaption}
                editorId={`${elementId}-${TABLE_CAPTION_EDITOR_KEY}`}
                editorKey={TABLE_CAPTION_EDITOR_KEY}
            />
            <AttachedCommentsDropTarget
                setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
                gridSize={gridSize}
                isPresentational={isPresentational}
                element={element}
                elementId={elementId}
                getContextZoomScale={getContextZoomScale}
            />
            <AttachedCommentsContainer
                gridSize={gridSize}
                elementId={elementId}
                element={element}
                isPresentational={isPresentational}
                currentBoardId={currentBoardId}
                permissions={permissions}
            />

            <ElementResizeHandle {...props} showHandle={showResizeHandle} />
        </div>
    );
};

Table.propTypes = {
    element: PropTypes.object.isRequired,
    gridSize: PropTypes.number,
    inList: PropTypes.string,
    inTrash: PropTypes.bool,
    isLocked: PropTypes.bool,
    isSelected: PropTypes.bool,
    isEditable: PropTypes.bool,
    isPresentational: PropTypes.bool,
    isDragging: PropTypes.bool,
    isRemotelySelected: PropTypes.bool,
    permissions: PropTypes.number,
    currentBoardId: PropTypes.string,
    elementEvents: PropTypes.object,
    tempSize: PropTypes.object,
    setParentHoveredChildAcceptsDrop: PropTypes.func,
    getContextZoomScale: PropTypes.func,
    getContextZoomTranslationPx: PropTypes.func,

    dispatchStartEditingTitle: PropTypes.func,
    dispatchSetCellSelection: PropTypes.func,
    hotTableInstanceRef: PropTypes.object,
    hotTableContainerRef: PropTypes.object,
    currentCellSelections: PropTypes.object,
};

export default Table;
